import { Link, useParams } from "react-router-dom";
import {
  ArrowRight,
  MapPin,
  Clock,
  Car,
  CheckCircle2,
  Phone,
} from "lucide-react";
import WhatsAppButton from "../components/site/WhatsAppButton";
import LeadForm from "../components/site/LeadForm";
import Seo, { localBusinessLd, faqLd } from "../lib/seo";
import { CLINIC } from "../data/content";

// Area-specific SEO content. Kept in one place so all local pages stay
// consistent and easy to expand later (add another area = add a row here).
export const AREAS = {
  najafgarh: {
    slug: "najafgarh",
    name: "Najafgarh",
    zone: "South West Delhi",
    distanceKm: 8,
    driveMinutes: 20,
    landmark: "just off the Najafgarh–Nangloi Road",
    keywords: "physiotherapy in Najafgarh, physio clinic near Najafgarh, sports rehab Najafgarh",
    intro:
      "Physio Hyve Clinic serves patients from Najafgarh and surrounding South-West Delhi neighbourhoods. Our clinic is a short drive up the Najafgarh–Nangloi Road, making it an easy visit for back pain, sports injuries, post-surgical rehab and neurological recovery.",
    localReasons: [
      "One straight drive up the Najafgarh–Nangloi Road — around 20 minutes in normal traffic.",
      "Structured, evidence-informed rehabilitation with a Fellowship-trained clinic head.",
      "Dedicated women's & maternal care programme for expectant and new mothers from Najafgarh.",
      "Assessments include clear timelines, honest outcomes and a realistic recovery plan.",
    ],
    connections: [
      "Najafgarh Metro (Grey Line)",
      "Dhansa Bus Stand",
      "Najafgarh Firni Road",
      "Kair, Ujwa & Mitraon villages",
    ],
    faqs: [
      {
        q: "How far is Physio Hyve Clinic from Najafgarh?",
        a: "The clinic is approximately 8 km from Najafgarh — usually a 15–20 minute drive up the Najafgarh–Nangloi Road, depending on traffic.",
      },
      {
        q: "Do you treat sports injuries for patients from Najafgarh?",
        a: "Yes. Sports rehabilitation is a special interest of our clinic head, Dr. Amit Sharma (Fellowship in Sports Rehabilitation, Level II). We regularly see athletes and active patients from Najafgarh.",
      },
      {
        q: "Do you offer maternal care for mothers from Najafgarh?",
        a: "Yes. Dr. Manishika Patait leads a dedicated prenatal, postnatal, childbirth and lactation programme. Expectant and new mothers from Najafgarh regularly join our small-group and 1:1 sessions.",
      },
    ],
  },
  "uttam-nagar": {
    slug: "uttam-nagar",
    name: "Uttam Nagar",
    zone: "West Delhi",
    distanceKm: 6,
    driveMinutes: 15,
    landmark: "a short drive from Uttam Nagar West Metro Station",
    keywords: "physiotherapy in Uttam Nagar, best physio clinic Uttam Nagar, sports rehab Uttam Nagar",
    intro:
      "Physio Hyve Clinic is a trusted physiotherapy destination for patients from Uttam Nagar and neighbouring West Delhi. From back and neck pain to ACL rehabilitation and post-surgical recovery — we build a plan around your condition and goals.",
    localReasons: [
      "Approximately 6 km from Uttam Nagar — a 15-minute drive via Nangloi Road on most days.",
      "Direct Blue Line metro connection — Uttam Nagar West → Nangloi is a short ride.",
      "Fellowship-trained clinic head and specialised women's health team under one roof.",
      "Straightforward booking, transparent pricing and honest conversations about recovery.",
    ],
    connections: [
      "Uttam Nagar West Metro (Blue Line)",
      "Uttam Nagar East Metro",
      "Janakpuri West",
      "Vipin Garden, Bindapur, Sitapuri",
    ],
    faqs: [
      {
        q: "How do I reach Physio Hyve Clinic from Uttam Nagar?",
        a: "The easiest options are Blue Line Metro to Nangloi (about 15 minutes) or a direct drive on the Nangloi Road — around 6 km and 15 minutes by car in normal traffic.",
      },
      {
        q: "Do you handle ACL and post-surgical knee rehabilitation for Uttam Nagar patients?",
        a: "Yes. ACL rehabilitation, PCL rehabilitation and complex knee cases are a core focus of our clinic head, Dr. Amit Sharma. We provide pre-operative and post-operative programmes with objective return-to-sport testing.",
      },
      {
        q: "Do you offer prenatal and postnatal care for mothers from Uttam Nagar?",
        a: "Yes. Our maternal care programme with Dr. Manishika Patait covers prenatal exercise, childbirth education, postnatal recovery, yoga and lactation support.",
      },
    ],
  },
  rohini: {
    slug: "rohini",
    name: "Rohini",
    zone: "North West Delhi",
    distanceKm: 12,
    driveMinutes: 25,
    landmark: "an easy drive down Rohtak Road / NH-9 from Rohini",
    keywords: "physiotherapy in Rohini, physio clinic Rohini, sports rehab near Rohini",
    intro:
      "Physio Hyve Clinic supports patients from Rohini and North-West Delhi with structured, evidence-informed physiotherapy. Whether you're managing chronic back pain, recovering from an ACL surgery or looking for maternal care — we tailor rehabilitation to your goals.",
    localReasons: [
      "Direct access from Rohini via Rohtak Road / NH-9 — around 25 minutes by car.",
      "Fellowship-trained clinic head, 17+ years of clinical experience.",
      "Dedicated women's health and maternal care programme with an internationally-certified educator.",
      "Personalised assessment first — treatment plans are built around your condition, not templates.",
    ],
    connections: [
      "Rohini Sector 18, 19, 20, 21",
      "Rithala Metro (Red Line)",
      "Peeragarhi & Paschim Vihar",
      "Mangolpuri & Sultanpuri",
    ],
    faqs: [
      {
        q: "Is Physio Hyve Clinic accessible from Rohini?",
        a: "Yes. The clinic is around 12 km from Rohini and typically 20–25 minutes by car via Rohtak Road / NH-9. Patients from all Rohini sectors and Rithala regularly visit us.",
      },
      {
        q: "Do you provide neurological rehabilitation for patients from Rohini?",
        a: "Yes. We provide stroke rehabilitation, balance and gait retraining, and family-supported neuro programmes. Home-programme guidance is included where appropriate.",
      },
      {
        q: "Can I get a physiotherapy consultation without a doctor's referral?",
        a: "Yes. No referral is needed. You can book an assessment directly. If you have MRI / X-ray reports, please bring them along.",
      },
    ],
  },
  peeragarhi: {
    slug: "peeragarhi",
    name: "Peeragarhi",
    zone: "West Delhi",
    distanceKm: 4,
    driveMinutes: 10,
    landmark: "a short drive down Rohtak Road from Peeragarhi Chowk",
    keywords: "physiotherapy in Peeragarhi, physio clinic Peeragarhi, sports rehab Peeragarhi",
    intro:
      "Physio Hyve Clinic is one of the closest evidence-informed physiotherapy destinations for patients from Peeragarhi. From back pain and sports injuries to ACL rehabilitation and neurological recovery — we build plans around your condition and daily life.",
    localReasons: [
      "Only ~4 km from Peeragarhi — usually a 10-minute drive on Rohtak Road.",
      "Direct Green Line metro connection — Peeragarhi → Nangloi is a short ride.",
      "Fellowship-trained clinic head (Sports Rehabilitation, Level II) and a dedicated maternal care team.",
      "Honest assessments, transparent plans and structured recovery timelines.",
    ],
    connections: [
      "Peeragarhi Metro (Green Line)",
      "Paschim Vihar East & West Metro",
      "Udyog Nagar",
      "Mangolpuri & Sultanpuri",
    ],
    faqs: [
      {
        q: "How close is Physio Hyve Clinic to Peeragarhi?",
        a: "The clinic is approximately 4 km from Peeragarhi, typically a 10-minute drive on Rohtak Road. Green Line metro to Nangloi is a convenient alternative.",
      },
      {
        q: "Do you handle sports injuries and ACL cases for patients from Peeragarhi?",
        a: "Yes. Our clinic head Dr. Amit Sharma is Fellowship-trained in Sports Rehabilitation (Level II) and regularly manages ACL, PCL and post-surgical knee cases with objective return-to-sport testing.",
      },
      {
        q: "Do you offer maternal and prenatal care for mothers from Peeragarhi?",
        a: "Yes. Dr. Manishika Patait leads prenatal exercise, postnatal recovery, childbirth education, yoga and lactation programmes — many of our mothers travel from Peeragarhi and neighbouring West Delhi.",
      },
    ],
  },
  "paschim-vihar": {
    slug: "paschim-vihar",
    name: "Paschim Vihar",
    zone: "West Delhi",
    distanceKm: 5,
    driveMinutes: 12,
    landmark: "a short drive from Paschim Vihar East / West Metro",
    keywords: "physiotherapy in Paschim Vihar, physio clinic Paschim Vihar, sports rehab Paschim Vihar",
    intro:
      "Physio Hyve Clinic serves patients from Paschim Vihar and surrounding West Delhi with modern, evidence-informed rehabilitation. Personalised assessment, honest planning and structured recovery — for pain, injuries, post-surgical cases and maternal care.",
    localReasons: [
      "Approximately 5 km from Paschim Vihar — usually a 10–12 minute drive.",
      "Direct Green Line metro access from Paschim Vihar East / West to Nangloi.",
      "Fellowship-trained clinic head, 17+ years of experience across sports, post-surgical and neuro rehab.",
      "Dedicated women's health and maternal wellness programme under one roof.",
    ],
    connections: [
      "Paschim Vihar East Metro (Green Line)",
      "Paschim Vihar West Metro",
      "Madipur & Shivaji Park",
      "Peeragarhi & Rohtak Road",
    ],
    faqs: [
      {
        q: "How far is Physio Hyve Clinic from Paschim Vihar?",
        a: "The clinic is around 5 km away — usually a 10–12 minute drive on Rohtak Road. Metro (Green Line) to Nangloi is a comfortable alternative.",
      },
      {
        q: "Do you provide post-surgical rehabilitation for patients from Paschim Vihar?",
        a: "Yes. We provide structured post-surgical rehabilitation after orthopaedic and joint-replacement procedures — with clear, phase-based recovery milestones.",
      },
      {
        q: "Can I book an assessment without a doctor's referral?",
        a: "Yes. No referral is required. You can book an assessment directly and bring along any MRI / X-ray reports if available.",
      },
    ],
  },
  janakpuri: {
    slug: "janakpuri",
    name: "Janakpuri",
    zone: "West Delhi",
    distanceKm: 7,
    driveMinutes: 18,
    landmark: "an easy drive via Najafgarh Road from Janakpuri",
    keywords: "physiotherapy in Janakpuri, physio clinic Janakpuri, sports rehab Janakpuri",
    intro:
      "Physio Hyve Clinic is a trusted destination for patients from Janakpuri and neighbouring West Delhi. Whether it's chronic back pain, an ACL rehab plan or maternal care with a certified educator — every plan starts with an honest assessment.",
    localReasons: [
      "Approximately 7 km from Janakpuri — a 15–18 minute drive on most days.",
      "Direct Blue Line and Magenta Line metro access via Janakpuri West → Nangloi.",
      "Fellowship-trained clinic head with 17+ years of sports and post-surgical rehab experience.",
      "CAPPA-certified maternal care educator — prenatal, postnatal, childbirth and lactation support.",
    ],
    connections: [
      "Janakpuri West Metro (Blue / Magenta Line)",
      "Janakpuri East Metro",
      "Vikaspuri",
      "Uttam Nagar & Tilak Nagar",
    ],
    faqs: [
      {
        q: "How do I reach Physio Hyve Clinic from Janakpuri?",
        a: "You can drive via Najafgarh Road — around 7 km and 15–18 minutes typically. Blue or Magenta Line metro to Nangloi is a reliable alternative on busy days.",
      },
      {
        q: "Do you provide back and neck pain physiotherapy for Janakpuri patients?",
        a: "Yes. Back pain and cervical (neck) pain are among our most common presentations — with evidence-informed manual therapy, exercise and movement retraining.",
      },
      {
        q: "Is there a dedicated women's health programme for expectant mothers?",
        a: "Yes. Dr. Manishika Patait leads a comprehensive programme covering prenatal & postnatal exercise, yoga, childbirth education and lactation support.",
      },
    ],
  },
};

export default function LocationPage() {
  const { area } = useParams();
  const A = AREAS[area];
  if (!A) {
    return (
      <div className="container-wide py-24 text-center">
        <h1 className="font-heading text-3xl text-brand-ink">Area page not found</h1>
        <Link to="/" className="btn-outline mt-6">Back home</Link>
      </div>
    );
  }

  const waMsg = `Hello Physio Hyve, I'm from ${A.name} and would like to enquire about physiotherapy.`;

  return (
    <div>
      <Seo
        title={`Physiotherapy in ${A.name} | Physio Hyve Clinic — ${A.distanceKm} km from ${A.name}`}
        description={`Trusted physiotherapy clinic serving ${A.name}, ${A.zone}. Sports rehab, ACL, back pain, neuro rehabilitation and maternal care. ${A.distanceKm} km from ${A.name}, ${A.driveMinutes} min drive. Fellowship-trained clinic head, 4.8★ Google rating.`}
        path={`/areas/${A.slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              ...localBusinessLd(),
              areaServed: [
                { "@type": "Place", name: A.name },
                { "@type": "Place", name: A.zone },
                { "@type": "Place", name: "Nangloi" },
              ],
            },
            faqLd(A.faqs),
          ],
        }}
      />

      {/* Hero */}
      <section className="container-wide pt-16 pb-10">
        <div className="max-w-3xl">
          <div className="eyebrow mb-3 flex items-center gap-2">
            <MapPin size={14} /> Serving {A.name} · {A.zone}
          </div>
          <h1 className="font-heading text-4xl md:text-6xl text-brand-ink leading-[1.05]">
            Physiotherapy in <span className="text-brand-terracotta">{A.name}</span> — care built around your recovery.
          </h1>
          <p className="text-brand-ink2 mt-6 text-lg">{A.intro}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="#area-enquiry"
              className="btn-primary"
              data-testid={`area-${A.slug}-enquire`}
            >
              Enquire from {A.name} <ArrowRight size={16} />
            </a>
            <WhatsAppButton message={waMsg} data-testid={`area-${A.slug}-whatsapp`}>
              Ask on WhatsApp
            </WhatsAppButton>
          </div>
          <div className="mt-8 flex flex-wrap gap-6 text-sm text-brand-ink2">
            <span className="flex items-center gap-2">
              <Car size={14} className="text-brand-olive" /> ~{A.distanceKm} km from {A.name}
            </span>
            <span className="flex items-center gap-2">
              <Clock size={14} className="text-brand-olive" /> ~{A.driveMinutes} minute drive
            </span>
            <span className="flex items-center gap-2">
              <MapPin size={14} className="text-brand-olive" /> {A.landmark}
            </span>
          </div>
        </div>
      </section>

      {/* Why local patients choose us */}
      <section className="bg-brand-cream/40 py-14">
        <div className="container-wide grid md:grid-cols-2 gap-10">
          <div>
            <div className="eyebrow mb-3">Why patients from {A.name} choose Physio Hyve</div>
            <h2 className="font-heading text-3xl md:text-4xl text-brand-ink leading-tight">
              Local, honest, evidence-informed care.
            </h2>
            <ul className="mt-6 space-y-3 text-brand-ink2">
              {A.localReasons.map((t) => (
                <li key={t} className="flex gap-3">
                  <CheckCircle2 size={16} className="mt-1 text-brand-olive shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6">
              <div className="text-xs uppercase tracking-widest text-brand-terracotta mb-2">
                Nearby areas we regularly serve
              </div>
              <div className="flex flex-wrap gap-1.5">
                {A.connections.map((c) => (
                  <span
                    key={c}
                    className="text-xs bg-white text-brand-ink2 rounded-full px-2.5 py-1 border border-brand-cream"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="card-soft p-6" id="area-enquiry">
            <div className="font-heading text-xl text-brand-ink">
              Enquire from {A.name}
            </div>
            <p className="text-sm text-brand-ink2 mt-2">
              Share a few details — we'll get back with the next step, expected timelines and how
              to reach the clinic from {A.name}.
            </p>
            <div className="mt-5">
              <LeadForm
                category="general"
                sourcePage={`area-${A.slug}`}
                defaultConcern={`Enquiry from ${A.name} — please share how you can help.`}
                compact
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services strip */}
      <section className="container-wide py-14">
        <div className="max-w-2xl">
          <div className="eyebrow mb-3">What we treat for patients from {A.name}</div>
          <h2 className="font-heading text-3xl md:text-4xl text-brand-ink leading-tight">
            One clinic, a full spectrum of rehabilitation care.
          </h2>
        </div>
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { to: "/services/sports-rehab", title: "Sports Rehabilitation", body: "Return-to-sport testing led by a Fellowship-trained clinic head." },
            { to: "/services/acl-rehab", title: "ACL Rehabilitation", body: "Pre- and post-operative programmes with objective milestones." },
            { to: "/services/back-pain", title: "Back Pain Physiotherapy", body: "Evidence-informed care for acute, chronic and radicular pain." },
            { to: "/services/neuro-rehab", title: "Neurological Rehabilitation", body: "Stroke, balance and functional retraining." },
            { to: "/services/post-surgical", title: "Post-Surgical Rehab", body: "Structured recovery after orthopaedic surgery." },
            { to: "/maternal-care", title: "Maternal Care", body: "Prenatal, postnatal, childbirth & lactation education." },
          ].map((s) => (
            <Link
              key={s.to}
              to={s.to}
              className="card-soft p-5 hover:shadow-[0_16px_50px_rgb(0,0,0,0.08)] transition-shadow group"
              data-testid={`area-${A.slug}-svc-${s.to.split("/").pop()}`}
            >
              <h3 className="font-heading text-lg text-brand-ink group-hover:text-brand-olive">
                {s.title}
              </h3>
              <p className="text-sm text-brand-ink2 mt-1.5">{s.body}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-xs text-brand-olive">
                Learn more <ArrowRight size={12} />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="container-wide pb-12 max-w-4xl">
        <div className="eyebrow mb-3">Frequently asked — {A.name}</div>
        <h2 className="font-heading text-3xl md:text-4xl text-brand-ink leading-tight">
          Answers for patients enquiring from {A.name}.
        </h2>
        <div className="mt-8 space-y-3">
          {A.faqs.map((f) => (
            <details key={f.q} className="card-soft p-5 group" data-testid={`area-${A.slug}-faq-${f.q.slice(0, 12).replace(/\s+/g, "-")}`}>
              <summary className="cursor-pointer list-none flex items-center justify-between font-medium text-brand-ink">
                <span>{f.q}</span>
                <span className="text-brand-terracotta group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-brand-ink2 text-sm leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="container-wide pb-24">
        <div className="rounded-[24px] bg-brand-blue-dark text-brand-cream p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
          <div>
            <div className="eyebrow text-brand-amber">Visiting from {A.name}?</div>
            <h3 className="font-heading text-2xl md:text-3xl mt-2">
              Let's plan your first assessment — clearly and honestly.
            </h3>
            <p className="text-brand-cream/80 mt-2 max-w-xl text-sm">
              Send us a short enquiry or a WhatsApp message. We&apos;ll guide you on the next step,
              what to bring, and how to reach the clinic from {A.name}.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href="#area-enquiry" className="btn-primary" data-testid={`area-${A.slug}-final-enquire`}>
              Send an Enquiry
            </a>
            <a
              href={`tel:${CLINIC.phoneRaw}`}
              className="btn-outline"
              data-testid={`area-${A.slug}-final-call`}
            >
              <Phone size={14} /> Call {CLINIC.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
